/**
 * Surface Thresholds Types
 * Temperature safety-margin thresholds used for thermal analysis margin checks.
 * Thresholds are shared per subsystem displayName (all placements of the same
 * named component share one threshold set) rather than per physical instance.
 */

export interface ThresholdRange {
  min: number | null;
  max: number | null;
}

export interface Thresholds {
  nonOperational: ThresholdRange;
  turnOn: ThresholdRange;
  qualification: ThresholdRange;
  acceptance: ThresholdRange;
  thermalDesign: ThresholdRange;
  operatingLimits: ThresholdRange;
}

/** Keyed by SubsystemPlacementData.displayName — shared across every placement of that component. */
export type SubsystemThresholdsMap = Record<string, Thresholds>;

export interface ThresholdLevelDef {
  key: keyof Thresholds;
  label: string;
}

export const THRESHOLD_LEVELS: ThresholdLevelDef[] = [
  { key: "nonOperational", label: "Non-Operational" },
  { key: "turnOn", label: "Turn ON" },
  { key: "qualification", label: "Qualification" },
  { key: "acceptance", label: "Acceptance" },
  { key: "thermalDesign", label: "Thermal Design" },
  { key: "operatingLimits", label: "Operating Limits" },
];

export const createEmptyThresholds = (): Thresholds => ({
  nonOperational: { min: null, max: null },
  turnOn: { min: null, max: null },
  qualification: { min: null, max: null },
  acceptance: { min: null, max: null },
  thermalDesign: { min: null, max: null },
  operatingLimits: { min: null, max: null },
});

export const TEMP_MIN_LIMIT = -200;
export const TEMP_MAX_LIMIT = 200;

export interface ValidationError {
  isValid: boolean;
  error?: string;
}

export const validateTemperatureValue = (value: number | null | string): ValidationError => {
  if (value === null || value === undefined || value === "") {
    return { isValid: true };
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (Number.isNaN(numValue)) {
    return { isValid: false, error: "Invalid number" };
  }

  if (!Number.isInteger(numValue)) {
    return { isValid: false, error: "Must be an integer (no decimals)" };
  }

  if (numValue < TEMP_MIN_LIMIT || numValue > TEMP_MAX_LIMIT) {
    return { isValid: false, error: `Must be between ${TEMP_MIN_LIMIT}°C and ${TEMP_MAX_LIMIT}°C` };
  }

  return { isValid: true };
};

export const validateThresholdRange = (min: number | null, max: number | null): ValidationError => {
  if (min === null || max === null) {
    return { isValid: true };
  }

  if (min > max) {
    return { isValid: false, error: "Minimum must be less than or equal to maximum" };
  }

  return { isValid: true };
};

export const validateAllThresholds = (thresholds: Thresholds): ValidationError => {
  for (const levelKey of THRESHOLD_LEVELS) {
    const level = thresholds[levelKey.key];
    if (level.min !== null || level.max !== null) {
      const minValidation = validateTemperatureValue(level.min);
      if (!minValidation.isValid) {
        return { isValid: false, error: `${levelKey.label} Min: ${minValidation.error}` };
      }

      const maxValidation = validateTemperatureValue(level.max);
      if (!maxValidation.isValid) {
        return { isValid: false, error: `${levelKey.label} Max: ${maxValidation.error}` };
      }

      const rangeValidation = validateThresholdRange(level.min, level.max);
      if (!rangeValidation.isValid) {
        return { isValid: false, error: `${levelKey.label}: ${rangeValidation.error}` };
      }
    }
  }

  return { isValid: true };
};
